import { envBasedConfig } from "@/constants/env-based-config.constant";
import { useEffect, useState } from "react";
import { remoteLogger } from "@/utils/remoteLogger";
import { decodeResponse } from "@/utils/helpers/decodeResponse";
import { optional, string, type } from "superstruct";
import type { Infer } from "superstruct";
import { isConsentGivenFor } from "@/modules/privacy";

export const ProfileStruct = type({
  name: string(),
  avatar: type({
    path: string(),
    alt: string(),
    darkModePath: string(),
  }),
  gender: optional(string()),
  dob: optional(string()),
});

export type Profile = Infer<typeof ProfileStruct>;

export function useProfile(): {
  status: number | null;
  profile: Profile | undefined;
  hasError: boolean;
} {
  const [status, setStatus] = useState<number | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const [profile, setProfile] = useState<Profile>();

  const getProfile = async () => {
    // get the profile from the API:q
    const headers: HeadersInit = {};
    const response = await fetch(`${envBasedConfig.profile}`, {
      headers,
    });
    setStatus(response.status);
    if (response.status === 404) {
      // empty list
      setHasError(false);

      return;
    }

    if (response.status === 200) {
      const [parseError, decodedHistory] = await decodeResponse(
        response,
        ProfileStruct,
      );
      // return the error
      if (parseError) {
        setHasError(true);
        remoteLogger.error(parseError);

        return;
      }
      // return the history and no error
      setHasError(false);
      setProfile(decodedHistory);

      return;
    }
    setHasError(true);

    remoteLogger.error(
      "Cannot get profile, see error message for an additional details.",
      `${response.status} ${response.statusText}`,
    );
  };

  useEffect(() => {
    const isConsentGiven = isConsentGivenFor("targeting");
    if (!isConsentGiven) {
      return;
    }
    void getProfile();
  }, []);

  return { status, profile, hasError };
}
